/**
 * pilotModel — pure rules for Pilot & requests (Bill 2026-11-04). Tested in __tests__/pilotModel.test.ts.
 *
 * Requests: the dashboard tiles, the plain status filter, search. Pilot testing: the filters, the per-section
 * Summary (the workbook's own), each item's one-line headline, the change record in words, and the life-ring
 * prefill for "Issue Identified".
 */
import type { PillVariant } from '@/components/primitives/Pill';
import type {
  PilotHistoryRow,
  PilotItem,
  PilotSection,
  RequestRow,
  RequestStage,
} from './pilotApi';

const lc = (s: string | null | undefined): string => (s ?? '').trim().toLowerCase();

// ── requests ───────────────────────────────────────────────────────────────────────────────────────────────

/** The plain status filter, in the order an item moves. */
export const STAGE_OPTIONS: ReadonlyArray<{ id: RequestStage | 'open' | 'any'; label: string }> = [
  { id: 'any', label: 'Any status' },
  { id: 'open', label: 'Still open' },
  { id: 'received', label: 'Reported, not looked at yet' },
  { id: 'looking', label: 'Being looked at' },
  { id: 'working', label: 'Being worked on' },
  { id: 'no-change', label: 'Decided: no change' },
  { id: 'live', label: 'Live in the app' },
  { id: 'checked', label: 'Live and checked' },
  { id: 'undo', label: 'Undo requested' },
  { id: 'undone', label: 'Change undone' },
  { id: 'closed', label: 'Closed' },
];

export const STAGE_TONE: Record<RequestStage, PillVariant> = {
  received: 'muted',
  looking: 'info',
  working: 'info',
  'no-change': 'muted',
  live: 'ok',
  checked: 'ok',
  undo: 'warn',
  undone: 'warn',
  closed: 'muted',
};

export interface RequestKpis {
  open: number;
  beingWorked: number;
  live: number;
  checked: number;
  /** null when the Hub would not say which items wait for a compliance review. */
  awaitingCompliance: number | null;
  mineThisWeek: number;
}

const WEEK_MS = 7 * 86_400_000;

export function requestKpis(rows: ReadonlyArray<RequestRow>, complianceKnown: boolean, now: Date = new Date()): RequestKpis {
  return {
    open: rows.filter((r) => r.open).length,
    beingWorked: rows.filter((r) => r.stage === 'looking' || r.stage === 'working' || r.stage === 'undo').length,
    live: rows.filter((r) => r.stage === 'live').length,
    checked: rows.filter((r) => r.stage === 'checked').length,
    awaitingCompliance: complianceKnown ? rows.filter((r) => r.awaitingCompliance === true).length : null,
    mineThisWeek: rows.filter((r) => r.mine && now.getTime() - new Date(r.requestedAt).getTime() <= WEEK_MS).length,
  };
}

export type KpiFilter = 'open' | 'working' | 'live' | 'checked' | 'compliance' | 'mine-week' | null;

export interface RequestFilter {
  stage: RequestStage | 'open' | 'any';
  category: string;
  from: string;
  to: string;
  q: string;
  /** A tile the person clicked. */
  tile: KpiFilter;
}

export const EMPTY_REQUEST_FILTER: RequestFilter = { stage: 'any', category: '', from: '', to: '', q: '', tile: null };

function day(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function filterRequests(rows: ReadonlyArray<RequestRow>, f: RequestFilter, now: Date = new Date()): RequestRow[] {
  const q = lc(f.q);
  return rows.filter((r) => {
    if (f.stage === 'open' && !r.open) return false;
    if (f.stage !== 'any' && f.stage !== 'open' && r.stage !== f.stage) return false;
    if (f.category && lc(r.category) !== lc(f.category)) return false;
    const asked = day(r.requestedAt);
    if (f.from && asked < f.from) return false;
    if (f.to && asked > f.to) return false;
    if (q && !lc(r.key).includes(q) && !lc(r.summary).includes(q)) return false;
    switch (f.tile) {
      case 'open': return r.open;
      case 'working': return r.stage === 'looking' || r.stage === 'working' || r.stage === 'undo';
      case 'live': return r.stage === 'live';
      case 'checked': return r.stage === 'checked';
      case 'compliance': return r.awaitingCompliance === true;
      case 'mine-week': return r.mine && now.getTime() - new Date(r.requestedAt).getTime() <= WEEK_MS;
      default: return true;
    }
  });
}

export function requestCategories(rows: ReadonlyArray<RequestRow>): string[] {
  return [...new Set(rows.map((r) => (r.category ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

// ── pilot testing ──────────────────────────────────────────────────────────────────────────────────────────

export const STATUS_TONE: Record<string, PillVariant> = {
  'Not Started': 'muted',
  'In Progress': 'info',
  Complete: 'ok',
  'Issue Identified': 'err',
  'N/A': 'muted',
};

export function statusTone(status: string | null | undefined): PillVariant {
  return STATUS_TONE[status ?? ''] ?? 'muted';
}

export interface PilotFilter {
  sectionId: number | 'all';
  status: string;
  testerGroup: string;
  mine: boolean;
  q: string;
}

export const EMPTY_PILOT_FILTER: PilotFilter = { sectionId: 'all', status: '', testerGroup: '', mine: false, q: '' };

/** Status filter value for items the workbook gave no status (Test Scenarios / Test Results). */
export const NO_STATUS = '(none)';

export function isAssignedTo(item: Pick<PilotItem, 'assignedToUpn'>, upn: string | null | undefined): boolean {
  return !!upn && lc(item.assignedToUpn) === lc(upn);
}

export function filterItems(items: ReadonlyArray<PilotItem>, f: PilotFilter, me: string | null | undefined): PilotItem[] {
  const q = lc(f.q);
  return items.filter((i) => {
    if (f.sectionId !== 'all' && i.sectionId !== f.sectionId) return false;
    if (f.status === NO_STATUS && i.status) return false;
    if (f.status && f.status !== NO_STATUS && i.status !== f.status && i.result !== f.status) return false;
    if (f.testerGroup && lc(i.testerGroup) !== lc(f.testerGroup)) return false;
    if (f.mine && !isAssignedTo(i, me)) return false;
    if (q) {
      const hay = [i.code, i.area, i.title, i.steps, i.group, i.notes, i.assignedTo, i.accountTypes, ...i.details.map((d) => d.value), ...i.issues.map((x) => x.changeKey)]
        .map(lc).join(' \u0001 ');
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function testerGroups(items: ReadonlyArray<PilotItem>): string[] {
  return [...new Set(items.map((i) => (i.testerGroup ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

/** What the item tests, in one line. Scenario rows have no "Test Item": their key columns stand in. */
export function itemHeadline(item: PilotItem, section: Pick<PilotSection, 'kind'> | undefined): string {
  if (item.title?.trim()) return item.title.trim();
  if (section?.kind === 'scenarios' || section?.kind === 'results') {
    const pick = (label: string): string | null => item.details.find((d) => d.label === label)?.value?.trim() || null;
    const parts = section.kind === 'scenarios'
      ? [pick('Single or Joint'), pick('Customer Type'), item.accountTypes?.trim() || null]
      : [pick('Error Details'), pick('Change Request')];
    const line = parts.filter(Boolean).join(' · ').replace(/\s*\n\s*/g, ' / ');
    if (line) return line.length > 160 ? `${line.slice(0, 157)}…` : line;
  }
  return item.steps?.trim().split('\n')[0]?.slice(0, 160) || 'No description';
}

/** The section's heading for a column, falling back to plain words. */
export function columnLabel(section: Pick<PilotSection, 'columnLabels'> | undefined, field: string, fallback: string): string {
  return section?.columnLabels?.[field]?.trim() || fallback;
}

/** Sections the consumer pilot works first: phase 1, then the rest; Phase 2 starts collapsed. */
export function startsCollapsed(section: Pick<PilotSection, 'phase'>): boolean {
  return section.phase.trim() !== '1';
}

/** The Summary tab's totals across the given sections. */
export function summaryTotals(sections: ReadonlyArray<PilotSection>): PilotSection['counts'] {
  const z = { notStarted: 0, inProgress: 0, complete: 0, issueIdentified: 0, notApplicable: 0, total: 0, percentComplete: 0, pass: 0, fail: 0 };
  for (const s of sections) {
    z.notStarted += s.counts.notStarted;
    z.inProgress += s.counts.inProgress;
    z.complete += s.counts.complete;
    z.issueIdentified += s.counts.issueIdentified;
    z.notApplicable += s.counts.notApplicable;
    z.total += s.counts.total;
    z.pass += s.counts.pass;
    z.fail += s.counts.fail;
  }
  const denom = z.total - z.notApplicable;
  z.percentComplete = denom <= 0 ? 0 : Math.round((1000 * z.complete) / denom) / 10;
  return z;
}

const ACTION_WORDS: Record<string, string> = {
  loaded: 'Loaded from the workbook',
  added: 'Added',
  retired: 'Retired',
  restored: 'Restored',
  'issue-reported': 'Issue reported with the life ring',
  'section-added': 'Section added',
  'section-changed': 'Section changed',
  'section-retired': 'Section retired',
  'section-restored': 'Section restored',
};

/** One recorded change in words: "Status: Not Started → In Progress". */
export function historyWords(h: Pick<PilotHistoryRow, 'action' | 'field' | 'fieldLabel' | 'oldValue' | 'newValue'>): string {
  if (h.action === 'changed' || (h.action === 'section-changed' && h.field)) {
    const label = h.fieldLabel || h.field || 'Field';
    const from = h.oldValue?.trim() ? h.oldValue.trim() : '(blank)';
    const to = h.newValue?.trim() ? h.newValue.trim() : '(blank)';
    return `${label}: ${clip(from)} → ${clip(to)}`;
  }
  const base = ACTION_WORDS[h.action] ?? h.action;
  return h.newValue?.trim() ? `${base}: ${clip(h.newValue.trim())}` : base;
}

function clip(s: string): string {
  const one = s.replace(/\s*\n\s*/g, ' / ');
  return one.length > 140 ? `${one.slice(0, 137)}…` : one;
}

/** "Issue Identified": the life-ring form prefilled with the test, so the report lands in the tracker naming it. */
export function issuePrefill(item: PilotItem, section: PilotSection | undefined): { kind: 'broken'; attempting: string; expected: string; context: string } {
  const id = item.code?.trim() || '(no ID)';
  const what = itemHeadline(item, section);
  const where = section?.name ? ` (${section.name})` : '';
  const steps = item.steps?.trim() ?? '';
  const expectedAt = steps.indexOf('Expected:');
  return {
    kind: 'broken',
    attempting: `Pilot test ${id}${where}: ${what}`,
    expected: expectedAt >= 0 ? steps.slice(expectedAt + 'Expected:'.length).trim() : '',
    context: `From pilot test ${id}: ${what}. Say what went wrong; the report is tied to this test when you send it.`,
  };
}

/** The report id the event write returned ({ ids: [id] }), or null. */
export function reportIdFrom(result: unknown): string | null {
  if (!result || typeof result !== 'object') return null;
  const ids = (result as { ids?: unknown }).ids;
  return Array.isArray(ids) && typeof ids[0] === 'string' && ids[0] ? ids[0] : null;
}

/** "jessica.beucler@mypcb.com" -> "Jessica Beucler" when no name was recorded. */
export function nameOf(upn: string | null | undefined, name?: string | null): string {
  if (name?.trim() && !name.includes('@')) return name.trim();
  const u = (upn ?? '').trim();
  if (!u) return '—';
  if (u.startsWith('seed-')) return 'Workbook load';
  return (u.split('@')[0] ?? u).split(/[._-]+/).filter(Boolean).map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
}

export function shortDay(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function whenText(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}
